// Renderizador del horario de atención de la sucursal.
//
// Ticket #59 — FUENTE ÚNICA. Lee las filas de `goauto_sched.dealership_hours`
// (lo que la automotora configura en Agenda → Configuración → Horario del
// admin), servidas por la RPC `fn_dealership_public_hours`. Antes leía
// `dealerships.opening_hours`, un JSONB paralelo que se editaba en otra
// pantalla y que en 7 de 8 sucursales decía algo distinto del horario real.
//
// Los siete días se listan SIEMPRE, uno por fila y con el nombre completo. Lo
// que cambia es de dónde sale el dato y cómo se distingue "cerrado" de "sin
// declarar":
//
//   · fila con is_open = true   → "09:00 a 18:30"
//   · fila con is_open = false  → "Cerrado"
//   · SIN fila para ese día     → "Consultar"
//
// El tercer caso es el del ticket original. En `dealership_hours` no hay un
// tercer valor dentro de la fila —`is_open` es boolean—, así que "sin declarar"
// se representa con la ausencia de la fila. Dar por cerrado un día que nadie
// declaró sería publicar un dato que la automotora no eligió.
//
// GEMELO de goautos-admin/src/components/builder2/sections/contact/OpeningHours.tsx:
// el editor y el sitio publicado tienen que mostrar lo mismo. Si cambia uno,
// cambia el otro.

import { PublicDayHours } from '@/lib/dealership-hours';
import useClientStore from '@/store/useClientStore';
import { forcedSiteLanguage, horaDelPais } from '@/lib/country';

// Orden de lectura: la semana arranca el lunes, el domingo va al final.
const DAYS: { weekday: number; label: string }[] = [
  { weekday: 1, label: 'Lunes' },
  { weekday: 2, label: 'Martes' },
  { weekday: 3, label: 'Miércoles' },
  { weekday: 4, label: 'Jueves' },
  { weekday: 5, label: 'Viernes' },
  { weekday: 6, label: 'Sábado' },
  { weekday: 0, label: 'Domingo' },
];

/**
 * Brasil (por país del tenant). Va por el store del tenant y no por i18n a
 * propósito: este componente también se monta suelto (harness del horario,
 * ContactForm) y ahí no hay provider de idioma; sin tenant de Brasil todo
 * sale exactamente como siempre.
 */
const DAYS_PT: Record<number, string> = {
  1: 'Segunda',
  2: 'Terça',
  3: 'Quarta',
  4: 'Quinta',
  5: 'Sexta',
  6: 'Sábado',
  0: 'Domingo',
};

/** EE.UU. (por país del tenant, mismo criterio que Brasil). */
const DAYS_EN: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  0: 'Sunday',
};

type TextosHorario = {
  consultar: string;
  cerrado: string;
  a: string;
  y: string;
  /** Cómo se lee una hora 'HH:MM'. Sin él, tal cual (CL y BR, como siempre). */
  hora?: (hhmm: string) => string;
};
const TEXTOS_ES: TextosHorario = { consultar: 'Consultar', cerrado: 'Cerrado', a: 'a', y: 'y' };
const TEXTOS_PT: TextosHorario = { consultar: 'Consultar', cerrado: 'Fechado', a: 'às', y: 'e' };
// EE.UU.: 12 horas ("9:00 AM – 6:30 PM").
const TEXTOS_EN: TextosHorario = {
  consultar: 'Call for hours',
  cerrado: 'Closed',
  a: '–',
  y: 'and',
  hora: (hhmm) => horaDelPais(hhmm, 'US'),
};

/** 'HH:MM:SS' → 'HH:MM'. Devuelve '' si no hay hora utilizable. */
const hhmm = (t?: string | null): string =>
  typeof t === 'string' && /^\d{2}:\d{2}/.test(t) ? t.slice(0, 5) : '';

// Texto de la fila. `undefined` = día sin declarar (no hay fila para él).
const formatDay = (row?: PublicDayHours, tx: TextosHorario = TEXTOS_ES): string => {
  if (!row) return tx.consultar;
  if (!row.is_open) return tx.cerrado;

  const h = tx.hora ?? ((s: string) => s);
  const open = hhmm(row.open_time);
  const close = hhmm(row.close_time);
  // Un día abierto sin las dos horas no es publicable: la fila existe pero no
  // dice nada útil, así que se trata como sin declarar en vez de inventar.
  if (!open || !close) return tx.consultar;

  // Corte de almuerzo: sólo si vienen las dos puntas y caen dentro del tramo.
  // Se muestra como dos tramos, que es lo que la sucursal realmente atiende.
  const bStart = hhmm(row.break_start);
  const bEnd = hhmm(row.break_end);
  if (bStart && bEnd && bStart > open && bEnd < close && bStart < bEnd) {
    return `${h(open)} ${tx.a} ${h(bStart)} ${tx.y} ${h(bEnd)} ${tx.a} ${h(close)}`;
  }
  return `${h(open)} ${tx.a} ${h(close)}`;
};

interface OpeningHoursProps {
  hours?: PublicDayHours[] | null;
  labelColor?: string;
  valueColor?: string;
}

export default function OpeningHours({ hours, labelColor, valueColor }: OpeningHoursProps) {
  const forzado = forcedSiteLanguage(useClientStore((s) => s.client?.country));
  const esBrasil = forzado === 'pt';
  const esUsa = forzado === 'en';
  const tx = esBrasil ? TEXTOS_PT : esUsa ? TEXTOS_EN : TEXTOS_ES;

  // Sin ninguna fila no hay horario que mostrar: se oculta el bloque entero,
  // igual que antes. Siete "Consultar" serían ruido, no dato.
  if (!hours || hours.length === 0) return null;

  const byWeekday = new Map(hours.map((r) => [Number(r.weekday), r]));

  return (
    <div className="space-y-0.5">
      {DAYS.map(({ weekday, label }) => (
        <div key={weekday} className="flex gap-2 text-sm">
          {/* Ancho fijo de etiqueta + gap, NUNCA justify-between: la hora va
              pegada al día (fix 6020188). Son 6rem porque "Miércoles" no entra
              en los 5rem que había antes. */}
          <span
            className={!labelColor ? 'w-24 flex-shrink-0 text-gray-700 dark:text-gray-300' : 'w-24 flex-shrink-0'}
            style={labelColor ? { color: labelColor } : undefined}
          >
            {esBrasil ? DAYS_PT[weekday] : esUsa ? DAYS_EN[weekday] : label}
          </span>
          <span
            className={!valueColor ? 'text-gray-900 dark:text-white' : ''}
            style={valueColor ? { color: valueColor } : undefined}
          >
            {formatDay(byWeekday.get(weekday), tx)}
          </span>
        </div>
      ))}
    </div>
  );
}
